/**
 * SeededRandom — Deterministic PRNG (mulberry32) with state save/restore.
 * Used for NPC AI so all multiplayer peers make identical random decisions.
 */
export default class SeededRandom {
  /**
   * @param {number} seed - Integer seed value (non-zero)
   */
  constructor(seed) {
    this._state = seed | 0;
    if (this._state === 0) this._state = 1;
  }

  /**
   * Returns a float in [0, 1) — deterministic for given state.
   * @returns {number}
   */
  next() {
    this._state |= 0;
    this._state = (this._state + 0x6D2B79F5) | 0;
    let t = Math.imul(this._state ^ (this._state >>> 15), 1 | this._state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /**
   * Returns an integer in [min, max).
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  nextInt(min, max) {
    return min + Math.floor(this.next() * (max - min));
  }

  /** Save PRNG state for serialization */
  getState() { return this._state; }

  /** Restore PRNG state from serialization */
  setState(s) { this._state = s | 0; }

  /**
   * Convert a string to a deterministic numeric seed.
   * @param {string} str
   * @returns {number}
   */
  static hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const ch = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + ch;
      hash |= 0;
    }
    return hash || 1;
  }
}
